import React, { Component } from "react";
import PropTypes from "prop-types";
import { Table, Button, Modal, Icon } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { BackOfficeRoutes } from "@routes/urls";
import { toShortDateTime } from "@api/date";
import { DateTime } from "luxon";

export class ActivityLogItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showDetailsModal: false,
    };
  }

  handleDetailsClick = () => {
    this.setState({ showDetailsModal: true });
  };

  handleCloseModal = () => {
    this.setState({ showDetailsModal: false });
  };

  getActionIcon = (action) => {
    switch (action?.toLowerCase()) {
      case "created":
        return <Icon name="plus" color="green" />;
      case "updated":
        return <Icon name="edit" color="blue" />;
      case "deleted":
        return <Icon name="trash" color="red" />;
      default:
        return <Icon name="file outline" />;
    }
  };

  formatDateTime = (dateString) => {
    try {
      if (!dateString) return "-";
      return toShortDateTime(DateTime.fromISO(dateString));
    } catch (error) {
      return dateString || "-";
    }
  };

  getFieldDisplayName = (field) => {
    const fieldNames = {
      authors: "Authors",
      title: "Title",
      call_number: "Call Number",
      circulation: "Circulation",
      cover_metadata: "Cover Metadata",
      publication_year: "Publication Year",
      isbn: "ISBN",
      subjects: "Subjects",
      abstract: "Abstract",
      languages: "Languages",
      document_type: "Document Type",
      edition: "Edition",
      imprint: "Imprint",
      table_of_contents: "Table of Contents",
      notes: "Notes",
      urls: "URLs",
      alternative_titles: "Alternative Titles",
      alternative_abstracts: "Alternative Abstracts",
      extensions: "Extensions",
      tags: "Tags",
      restricted: "Restricted",
      identifiers: "Identifiers",
    };

    return fieldNames[field] || field.charAt(0).toUpperCase() + field.slice(1);
  };

  formatValue = (value) => {
    if (!value) return "";

    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === "object") {
        return value
          .map(
            (item) =>
              item.full_name ||
              item.name ||
              item.subject ||
              item.title ||
              String(item)
          )
          .join(", ");
      }
      return value.join(", ");
    }

    if (typeof value === "object") {
      if (value.$ref) return "[Reference]";
      if (value.title) return value.title;
      if (value.name) return value.name;
      if (value.value) return value.value;
      return "Complex data";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    return String(value);
  };

  formatFieldSummary = (fieldKey, newValue) => {
    if (!newValue) return "Field cleared";

    switch (fieldKey) {
      case "authors":
        if (Array.isArray(newValue)) {
          const authors = newValue
            .map((author) => author.full_name || author.name || author)
            .join(", ");
          return `Set to: ${authors}`;
        }
        return `Set to: ${newValue}`;

      case "title":
        return `Changed to: "${newValue}"`;

      case "call_number":
        return `Set to: ${newValue}`;

      case "publication_year":
        return `Set to: ${newValue}`;

      case "document_type":
        return `Changed to: ${newValue}`;

      case "subjects":
        if (Array.isArray(newValue)) {
          const subjects = newValue
            .map((subj) => subj.subject || subj)
            .join(", ");
          return `Set to: ${subjects}`;
        }
        return `Set to: ${newValue}`;

      default:
        if (typeof newValue === "object") {
          return "Updated (complex data)";
        }
        return `Set to: ${newValue}`;
    }
  };

  isEqual = (val1, val2) => {
    try {
      if (val1 === val2) return true;
      if (!val1 && !val2) return true;
      if (!val1 || !val2) return false;

      if (typeof val1 === "object" && typeof val2 === "object") {
        return JSON.stringify(val1) === JSON.stringify(val2);
      }

      return false;
    } catch (error) {
      return val1 === val2;
    }
  };

  isEmpty = (value) => {
    if (value === null || value === undefined || value === "") {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    if (typeof value === "object" && Object.keys(value).length === 0) {
      return true;
    }
    return false;
  };

  parseChanges = (changesData) => {
    try {
      if (!changesData) return [];

      // Handle new simple string-based format
      if (changesData.descriptions && Array.isArray(changesData.descriptions)) {
        return changesData.descriptions.map((description, index) => ({
          id: index,
          description: description,
        }));
      }

      // Handle old format {field: {old: value, new: value}}
      if (
        typeof changesData === "object" &&
        !Array.isArray(changesData) &&
        !changesData.descriptions
      ) {
        const descriptions = [];

        Object.entries(changesData).forEach(([fieldKey, changeData]) => {
          if (
            changeData &&
            typeof changeData === "object" &&
            ("new" in changeData || "old" in changeData)
          ) {
            const fieldName = this.getFieldDisplayName(fieldKey);
            const newValue = changeData.new;

            // Only show if there's actually a change
            if (changeData.old !== changeData.new) {
              if (newValue) {
                descriptions.push(
                  `${fieldName} changed to: ${this.formatValue(newValue)}`
                );
              } else {
                descriptions.push(`${fieldName} cleared`);
              }
            }
          }
        });

        return descriptions.map((description, index) => ({
          id: index,
          description: description,
        }));
      }

      return [];
    } catch (error) {
      return [];
    }
  };

  renderUser = (auditLog) => {
    const { user_id: userId, user } = auditLog;

    if (!userId) {
      return <span>System</span>;
    }

    const fullName = user?.full_name || `User ${userId}`;

    return (
      <Link
        to={BackOfficeRoutes.patronDetailsFor(userId)}
        style={{ color: "black" }}
      >
        <Icon name="user" /> {fullName}
      </Link>
    );
  };

  renderActionButton = (action) => {
    if (action === "deleted") {
      return (
        <div
          data-tooltip="No details available for deleted documents"
          data-position="top center"
        >
          <Button size="small" basic disabled color="grey">
            No Details
          </Button>
        </div>
      );
    }

    return (
      <Button
        size="small"
        basic
        color="green"
        onClick={this.handleDetailsClick}
      >
        View Details
      </Button>
    );
  };

  renderCreationContent = () => {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          color: "#28a745",
          backgroundColor: "#f8fff9",
          borderRadius: "8px",
          border: "1px solid #d4edda",
        }}
      >
        <Icon name="plus circle" size="large" color="green" />
        <h4
          style={{
            color: "#28a745",
            marginTop: "16px",
            marginBottom: "8px",
          }}
        >
          Document Successfully Created
        </h4>
        <p style={{ fontSize: "1em", margin: "0", color: "#155724" }}>
          This document was added to the catalog with all required metadata and
          information.
        </p>
      </div>
    );
  };

  renderUpdateContent = (auditLog) => {
    const changes = this.parseChanges(auditLog.changes);

    if (changes.length > 0) {
      return (
        <div>
          <h4
            style={{
              color: "#f4b952",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Icon name="list" />
            Changes Made ({changes.length})
          </h4>

          <div
            style={{
              padding: "16px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "6px",
              borderLeft: "4px solid #f4b952",
            }}
          >
            {changes.map((change, index) => (
              <div
                key={change.id || index}
                style={{
                  marginBottom: index < changes.length - 1 ? "8px" : "0",
                  color: "#495057",
                  fontSize: "0.95em",
                  lineHeight: "1.4",
                }}
              >
                • {change.description}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          color: "#6c757d",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
        }}
      >
        <Icon name="info circle" size="large" color="grey" />
        <p style={{ marginTop: "16px", fontSize: "1.1em" }}>
          No field changes detected for this action.
        </p>
      </div>
    );
  };

  renderFallbackContent = () => {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          color: "#6c757d",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
        }}
      >
        <Icon name="info circle" size="large" color="grey" />
        <p style={{ marginTop: "16px", fontSize: "1.1em" }}>
          No additional details available for this action.
        </p>
      </div>
    );
  };

  renderModalContent = (auditLog) => {
    const { action } = auditLog;

    switch (action) {
      case "created":
        return this.renderCreationContent();
      case "updated":
        return this.renderUpdateContent(auditLog);
      default:
        return this.renderFallbackContent();
    }
  };

  renderDetailsModal = () => {
    const { auditLog } = this.props;
    const { showDetailsModal } = this.state;

    try {
      const isCreatedAction = auditLog.action === "created";

      return (
        <Modal
          open={showDetailsModal}
          onClose={this.handleCloseModal}
          size="small"
        >
          <Modal.Header>
            <Icon name={isCreatedAction ? "plus" : "edit"} />
            {isCreatedAction ? "Document Creation" : "Change Details"}
          </Modal.Header>

          <Modal.Content>
            <div style={{ marginBottom: "20px" }}>
              <p>
                <strong>Document:</strong> {auditLog.document_title}
              </p>
              <p>
                <strong>Document ID:</strong> {auditLog.document_id}
              </p>
              <p>
                <strong>User:</strong> {this.renderUser(auditLog)}
              </p>
              <p>
                <strong>Action:</strong> {auditLog.action}
              </p>
              <p>
                <strong>Date and Time:</strong>{" "}
                {this.formatDateTime(auditLog.created)}
              </p>
            </div>

            {this.renderModalContent(auditLog)}
          </Modal.Content>

          <Modal.Actions>
            <Button onClick={this.handleCloseModal} primary>
              <Icon name="close" />
              Close
            </Button>
          </Modal.Actions>
        </Modal>
      );
    } catch (error) {
      return (
        <Modal open={showDetailsModal} onClose={this.handleCloseModal}>
          <Modal.Header>Error</Modal.Header>
          <Modal.Content>Error displaying details.</Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleCloseModal}>Close</Button>
          </Modal.Actions>
        </Modal>
      );
    }
  };

  render() {
    const { auditLog } = this.props;

    try {
      const dateTime = this.formatDateTime(auditLog.created);
      const action = auditLog.action || "Unknown";
      const documentTitle = auditLog.document_title || "Unknown Document";
      const documentId = auditLog.document_id;

      return (
        <>
          <Table.Row>
            <Table.Cell width={3} textAlign="center">
              <div>
                <strong>{dateTime}</strong>
              </div>
            </Table.Cell>

            <Table.Cell width={3} textAlign="center">
              {this.renderUser(auditLog)}
            </Table.Cell>

            <Table.Cell width={2} textAlign="center">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  justifyContent: "center",
                }}
              >
                {this.getActionIcon(action)}
                {action.charAt(0).toUpperCase() + action.slice(1)}
              </div>
            </Table.Cell>

            <Table.Cell width={5} textAlign="center">
              {documentId ? (
                <Link
                  to={BackOfficeRoutes.documentDetailsFor(documentId)}
                  style={{ color: "black" }}
                >
                  {documentTitle}
                </Link>
              ) : (
                <span>{documentTitle}</span>
              )}
            </Table.Cell>

            <Table.Cell width={3} textAlign="center">
              {this.renderActionButton(auditLog.action)}
            </Table.Cell>
          </Table.Row>

          {this.renderDetailsModal()}
        </>
      );
    } catch (error) {
      return (
        <Table.Row>
          <Table.Cell colSpan={5} style={{ textAlign: "center", color: "red" }}>
            Error displaying this log entry
          </Table.Cell>
        </Table.Row>
      );
    }
  }
}

ActivityLogItem.propTypes = {
  auditLog: PropTypes.object.isRequired,
};
